'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { useAuth } from '@/lib/auth';
import { MEETING_MANAGEMENT_ROLES } from '@/features/meeting-attendance/constants/roles';
import { meetingAttendanceApi, type Pagination } from '@/services/api/meeting-attendance';
import { formatInstantWithTimezone, getUserTimezone } from '@/features/meeting-attendance/utils/timezone';
import { Calendar, Clock, MapPin, Plus, QrCode, Users } from 'lucide-react';

interface Meeting {
  id: string;
  title: string;
  description: string | null;
  startTime: string;
  endTime: string;
  location: string | null;
  type: string;
  status: string;
  timezone?: string;
  qrCodeOnline: string | null;
  qrCodeOffline: string | null;
  creator?: {
    id: string;
    displayName: string;
    email: string;
  } | null;
  attendances: Array<{
    id: string;
    status: string;
    user: {
      id: string;
      displayName: string;
      email: string;
    };
  }>;
  requiredAttendees?: Array<{ id: string }>;
}

export default function MeetingAttendanceMeetingsPage() {
  const { isReady } = useAuthGuard();
  const { t } = useTranslation();
  const { hasAnyRole, isAdmin } = useAuth();
  const router = useRouter();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('SCHEDULED');
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const filterOptions = useMemo(
    () => [
      { value: 'all', label: t.meetingAttendance.meetings.filterAll },
      { value: 'SCHEDULED', label: t.meetingAttendance.meetings.filterScheduled },
      { value: 'IN_PROGRESS', label: t.meetingAttendance.meetings.filterInProgress },
      { value: 'COMPLETED', label: t.meetingAttendance.meetings.filterCompleted },
    ],
    [t],
  );

  const canManage = isAdmin || hasAnyRole(MEETING_MANAGEMENT_ROLES);
  const displayTimezone = useMemo(() => getUserTimezone(), []);

  const fetchMeetings = useCallback(async () => {
    try {
      const params: Record<string, any> = {
        status: filter,
        page: pagination.page,
        limit: pagination.limit,
      };

      if (filter === 'all') {
        params.anchor = 'now';
      }

      const data = await meetingAttendanceApi.listMeetings(params);
      setMeetings(data.meetings || []);
      if (data.pagination) {
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch meeting list:', error);
    } finally {
      setLoading(false);
    }
  }, [filter, pagination.limit, pagination.page]);

  useEffect(() => {
    if (!isReady) {
      return;
    }
    fetchMeetings();
  }, [fetchMeetings, isReady]);

  const onChangeFilter = (status: string) => {
    setFilter(status);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setLoading(true);
  };

  const goPrev = () => {
    if (pagination.page > 1) {
      setPagination((prev) => ({ ...prev, page: prev.page - 1 }));
      setLoading(true);
    }
  };

  const goNext = () => {
    if (pagination.page < pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: prev.page + 1 }));
      setLoading(true);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      SCHEDULED: { label: t.meetingAttendance.status.scheduled, color: 'bg-blue-100 text-blue-800' },
      IN_PROGRESS: { label: t.meetingAttendance.status.inProgress, color: 'bg-green-100 text-green-800' },
      COMPLETED: { label: t.meetingAttendance.status.completed, color: 'bg-gray-100 text-gray-800' },
      CANCELLED: { label: t.meetingAttendance.status.cancelled, color: 'bg-red-100 text-red-800' },
    };
    const statusInfo = statusMap[status] || statusMap.SCHEDULED;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const getTypeBadge = (type: string) => {
    const typeMap: Record<string, { label: string; color: string }> = {
      OFFLINE: { label: t.meetingAttendance.type.offline, color: 'bg-purple-100 text-purple-800' },
      ONLINE: { label: t.meetingAttendance.type.online, color: 'bg-green-100 text-green-800' },
      HYBRID: { label: t.meetingAttendance.type.hybrid, color: 'bg-yellow-100 text-yellow-800' },
    };
    const typeInfo = typeMap[type] || typeMap.OFFLINE;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${typeInfo.color}`}>
        {typeInfo.label}
      </span>
    );
  };

  if (!isReady || loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t.meetingAttendance.common.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t.meetingAttendance.meetings.title}</h1>
          <p className="mt-1 text-sm text-gray-500">{t.meetingAttendance.meetings.subtitle}</p>
        </div>
        {canManage && (
          <Link href="/meetingattendance/meetings/create">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              {t.meetingAttendance.meetings.create}
            </Button>
          </Link>
        )}
      </div>

      <div className="flex space-x-2">
        {filterOptions.map((option) => (
          <Button
            key={option.value}
            variant={filter === option.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => onChangeFilter(option.value)}
          >
            {option.label}
          </Button>
        ))}
      </div>

      <div className="grid gap-6">
        {meetings.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Calendar className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t.meetingAttendance.meetings.emptyTitle}</h3>
              <p className="text-gray-500 text-center mb-4">
                {t.meetingAttendance.meetings.emptyDescription}
              </p>
              {canManage && (
                <Link href="/meetingattendance/meetings/create">
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    {t.meetingAttendance.meetings.create}
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          meetings.map((meeting) => {
            const presentCount = meeting.attendances.filter((item) => item.status === 'ON_SITE' || item.status === 'ONLINE').length;
            const lateCount = meeting.attendances.filter((item) => item.status === 'LATE').length;
            const absentCount = meeting.attendances.filter((item) => item.status === 'ABSENT').length;
            const requiredCount = meeting.requiredAttendees?.length ?? meeting.attendances.length;
            const startText = formatInstantWithTimezone(new Date(meeting.startTime), displayTimezone);
            const endText = formatInstantWithTimezone(new Date(meeting.endTime), displayTimezone);

            return (
              <Card
                key={meeting.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => router.push(`/meetingattendance/meetings/${meeting.id}`)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl">{meeting.title}</CardTitle>
                      {meeting.description && (
                        <CardDescription className="mt-1 line-clamp-1">
                          {meeting.description}
                        </CardDescription>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(meeting.status)}
                      {getTypeBadge(meeting.type)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600">
                    <div className="flex items-center">
                      <Clock className="mr-2 h-4 w-4" />
                      <div>
                        <div>{t.meetingAttendance.common.start}: {startText}</div>
                        <div>{t.meetingAttendance.common.end}: {endText}</div>
                      </div>
                    </div>
                    {meeting.location && (
                      <div className="flex items-center">
                        <MapPin className="mr-2 h-4 w-4" />
                        <span>{meeting.location}</span>
                      </div>
                    )}
                    <div className="flex items-center">
                      <Users className="mr-2 h-4 w-4" />
                      <div className="text-xs">
                        <div>{t.meetingAttendance.common.required}: {requiredCount}</div>
                        <div className="text-green-600">{t.meetingAttendance.common.present}: {presentCount}</div>
                        <div className="text-yellow-600">{t.meetingAttendance.common.late}: {lateCount}</div>
                        <div className="text-red-600">{t.meetingAttendance.common.absent}: {absentCount}</div>
                      </div>
                    </div>
                    {meeting.creator?.displayName && (
                      <div className="flex items-center">
                        <span className="text-xs">{t.meetingAttendance.common.creator}: {meeting.creator.displayName}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end space-x-2 mt-4">
                    <Link href={`/meetingattendance/meetings/${meeting.id}`}>
                      <Button variant="outline" size="sm" onClick={(event) => event.stopPropagation()}>
                        {t.meetingAttendance.common.viewDetails}
                      </Button>
                    </Link>
                    {(meeting.qrCodeOnline || meeting.qrCodeOffline) && (
                      <Link href={`/meetingattendance/meetings/${meeting.id}/qr`}>
                        <Button variant="outline" size="sm" onClick={(event) => event.stopPropagation()}>
                          <QrCode className="mr-2 h-4 w-4" />
                          {t.meetingAttendance.common.qrCodes}
                        </Button>
                      </Link>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between border-t pt-4">
          <div className="text-sm text-gray-600">
            {t.meetingAttendance.meetings.pageLabel
              .replace('{page}', String(pagination.page))
              .replace('{totalPages}', String(pagination.totalPages))
              .replace('{total}', String(pagination.total))}
          </div>
          <div className="space-x-2">
            <Button variant="outline" size="sm" onClick={goPrev} disabled={pagination.page <= 1}>
              {t.meetingAttendance.meetings.prevPage}
            </Button>
            <Button variant="outline" size="sm" onClick={goNext} disabled={pagination.page >= pagination.totalPages}>
              {t.meetingAttendance.meetings.nextPage}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
